import type { ImageCrop } from './types.js';
import type { FaceBox } from './faces.js';
import { coverPhotoFrame, photoCropRect } from './photo-framing.js';

/**
 * Automatic framing of one photo in one collage slot ("Kutije sa slikama").
 *
 * A collage photo keeps zoom 1 — the whole photo at cover fit — because a
 * collage is about the shopper's pictures, not about close-ups, and zooming a
 * 12 MP photo into a 5 cm card is how a print turns soft. At zoom 1 a cover
 * fit crops exactly ONE axis, so framing is a one-dimensional problem: slide
 * the window along that axis so the faces stay whole. With no faces a tall
 * photo keeps its upper part (cy 0.42), where heads and horizons sit.
 *
 * Pure and shared: the widget frames with it, the widget's board re-frames
 * with it after every move, Studio samples and agent review renders use it,
 * and the result is always what `coverPhotoFrame` (the one clamp the editor
 * and both renderers draw with) leaves unchanged — preview = print.
 */

/** A face smaller than this share of the photo's height is background, not a subject. */
export const COLLAGE_FACE_MIN_H = 0.06;
/** Where a tall photo without faces is centred when its height is cropped. */
export const COLLAGE_NO_FACE_CY = 0.42;
/**
 * How far a face box is grown before it must fit whole: the detector's box is
 * the face alone, and a crop at the hairline or the chin still looks cut.
 * Left/up by these shares of the face, then scaled to these sizes.
 */
const EXPAND = { left: 0.25, up: 0.55, width: 1.5, height: 1.85 } as const;
/** With room to spare above and below the faces, the share of it left above them. */
const HEADROOM = 0.35;
const EPS = 1e-9;

/** The part of a collage photo this file reads. */
export interface CollageCropPhoto {
  /** Upright pixel size of the original. */
  w?: number;
  h?: number;
  /** Fractions of the upright photo. */
  faces?: readonly FaceBox[];
}

export interface CollageCropResult {
  /** Undefined when the photo's size is unknown: the renderer then uses the centred cover fit. */
  crop: ImageCrop | undefined;
  /** Significant faces not fully inside the window. */
  facesCut: number;
}

type Size = { w: number; h: number };

function sized(photo: CollageCropPhoto): photo is CollageCropPhoto & Size {
  return typeof photo.w === 'number' && typeof photo.h === 'number' && photo.w > 0 && photo.h > 0;
}

/** Faces that are subjects: tall enough (`COLLAGE_FACE_MIN_H`) and with a real box. */
export function significantFaces(faces: readonly FaceBox[] | undefined): FaceBox[] {
  if (!Array.isArray(faces)) return [];
  return faces.filter((f) =>
    Number.isFinite(f.x) && Number.isFinite(f.y) && f.w > 0 && f.h >= COLLAGE_FACE_MIN_H);
}

/**
 * How many significant faces a framing cuts: faces not fully inside the part
 * of the photo the slot shows. Works for any crop (a hand framing, a zoom), so
 * the board's "face maybe cut" badge and the placement cost agree.
 */
export function collageFacesCut(
  photo: CollageCropPhoto,
  slot: Size,
  crop: ImageCrop | undefined,
  options: { faceFraming?: boolean } = {},
): number {
  if (options.faceFraming === false || !sized(photo) || !(slot.w > 0 && slot.h > 0)) return 0;
  const faces = significantFaces(photo.faces);
  if (faces.length === 0) return 0;
  const win = photoCropRect({ w: photo.w, h: photo.h }, slot, crop);
  return countCut(faces, win);
}

/** How far past the window edge a face may reach and still count as whole. */
const TOL = 1e-6;
/**
 * The same for a grown box: one step of the stored 3-decimal lattice. A grown
 * box is a matter of taste, and one clamped to the photo's edge could
 * otherwise never be "whole" when the window's edge falls between two steps.
 */
const GROWN_TOL = 1e-3;

/** Is the span [a, b] inside the window that starts at `start` and is `extent` long? */
function within(a: number, b: number, start: number, extent: number, tol = TOL): boolean {
  return a >= start - tol && b <= start + extent + tol;
}

function countCut(faces: readonly FaceBox[], win: { x: number; y: number; w: number; h: number }): number {
  let cut = 0;
  for (const f of faces) {
    const inside = within(f.x, f.x + f.w, win.x, win.w) && within(f.y, f.y + f.h, win.y, win.h);
    if (!inside) cut++;
  }
  return cut;
}

/**
 * The automatic framing of `photo` in a slot of `slot` size (the element's own
 * w/h, never its rotated box).
 *
 * - zoom 1, never rotated;
 * - the window's extent along the cropped axis comes from `coverPhotoFrame`;
 * - no faces: a horizontal crop is centred (cx .5), a vertical one keeps the
 *   upper part (cy .42);
 * - faces: the window position that keeps the most faces whole wins (the
 *   detector's boxes, exactly as the "face maybe cut" count measures them),
 *   then the most face area whole, then the most area of the faces grown
 *   for hair and chin; among equals the one nearest the faces' weighted
 *   centre. Vertically, spare room goes 35 % above the kept faces and the rest
 *   below (heads high, bodies below). When no face fits whole, the window
 *   centres on the faces themselves;
 * - on the 3-decimal lattice the answer stores and inside the range
 *   `coverPhotoFrame` clamps to, so the renderer draws exactly the window
 *   that was judged — no rounding moves a face out of it.
 */
export function collageAutoCrop(
  photo: CollageCropPhoto,
  slot: Size,
  options: { faceFraming?: boolean } = {},
): CollageCropResult {
  if (!sized(photo) || !(slot.w > 0 && slot.h > 0)) return { crop: undefined, facesCut: 0 };
  const source = { w: photo.w, h: photo.h };
  const base = coverPhotoFrame(source, slot, { cx: 0.5, cy: 0.5, zoom: 1 });
  // Exactly the extents coverPhotoFrame clamps with (sw / source.w), so the
  // inward rounding below lands inside the renderer's own bounds.
  const extentX = base.source.w / source.w;
  const extentY = base.source.h / source.h;
  const axis = extentX < 1 - EPS ? 'x' : extentY < 1 - EPS ? 'y' : null;
  const faces = options.faceFraming === false ? [] : significantFaces(photo.faces);
  let cx = 0.5;
  let cy = 0.5;
  if (axis) {
    const centre = faces.length > 0
      ? faceCentre(faces, axis, axis === 'x' ? extentX : extentY, axis === 'x' ? extentY : extentX)
      : (axis === 'x' ? 0.5 : COLLAGE_NO_FACE_CY);
    if (axis === 'x') cx = centre;
    else cy = centre;
  }
  const clamped = coverPhotoFrame(source, slot, { cx, cy, zoom: 1 }).crop;
  const crop: ImageCrop = { cx: roundInside(clamped.cx, extentX), cy: roundInside(clamped.cy, extentY), zoom: 1 };
  const win = { x: crop.cx - extentX / 2, y: crop.cy - extentY / 2, w: extentX, h: extentY };
  return { crop, facesCut: countCut(faces, win) };
}

/**
 * Rounds a clamped centre to 3 decimals without leaving [extent/2, 1 − extent/2],
 * the range `coverPhotoFrame` clamps to — a value rounded outward would be
 * moved again by the renderer and the stored framing would not be what prints.
 */
function roundInside(value: number, extent: number): number {
  const lo = extent / 2;
  const hi = 1 - extent / 2;
  const keep = (v: number) => Math.max(lo, Math.min(hi, v)) === v;
  let r = Math.round(value * 1000) / 1000;
  if (r < lo) r = Math.ceil(lo * 1000) / 1000;
  if (r > hi) r = Math.floor(hi * 1000) / 1000;
  if (Object.is(r, -0)) r = 0;
  return keep(r) ? r : value;
}

/** One significant face along the cropped axis, as runs of window centres (in thousandths). */
interface FaceRuns {
  /** Centres whose window holds the detector's box whole (what "whole" is judged by); empty when from > to. */
  raw: [from: number, to: number];
  /** Centres whose window holds the box grown for hair and chin, clamped to the photo. */
  grown: [from: number, to: number];
  /** Face area, the weight of keeping it. */
  weight: number;
  /** Centre of the grown box and of the box itself. */
  grownMid: number;
  mid: number;
  grownA: number;
  grownB: number;
}

/** What a window keeps: faces whole, raw face area whole, grown-face area whole. */
type Keep = [faces: number, area: number, grown: number];

function better(p: Keep, q: Keep): number {
  for (let i = 0; i < 3; i++) {
    if (p[i]! > q[i]! + 1e-12) return 1;
    if (p[i]! < q[i]! - 1e-12) return -1;
  }
  return 0;
}

function weightedMean(faces: readonly FaceRuns[], at: (f: FaceRuns) => number): number {
  const total = faces.reduce((sum, f) => sum + f.weight, 0);
  return faces.reduce((sum, f) => sum + at(f) * f.weight, 0) / (total || 1);
}

const inRun = (k: number, run: readonly [number, number]) => k >= run[0] && k <= run[1];

/**
 * The window centre on the cropped axis for a photo with faces, on the
 * 3-decimal lattice inside [extent/2, 1 − extent/2] (the renderer's clamp).
 *
 * Each face is kept whole over one run of lattice centres (found with the very
 * test `countCut` applies, so no rounding can disagree), so the best `Keep` is
 * found at the runs' ends, and every centre with that best `Keep` belongs to
 * one of a few kept sets, each a run of its own. Within its run each set takes
 * its preferred position (headroom rule or its faces' centre); the set nearest
 * the faces' weighted centre wins, ties to the smaller position.
 */
function faceCentre(faces: readonly FaceBox[], axis: 'x' | 'y', extent: number, otherExtent: number): number {
  const lo = extent / 2;
  const hi = 1 - extent / 2;
  let kLo = Math.ceil(lo * 1000 - 1e-9);
  if (kLo / 1000 < lo) kLo++;
  let kHi = Math.floor(hi * 1000 + 1e-9);
  if (kHi / 1000 > hi) kHi--;
  // The window leaves under a thousandth of the photo: nothing to choose.
  if (kLo > kHi) return 0.5;

  const startAt = (k: number) => k / 1000 - extent / 2;
  /** The centres k (in [kLo, kHi]) whose window holds [a, b]: `within` read at its two edges. */
  const runOf = (a: number, b: number, tol: number): [number, number] => {
    let to = Math.floor((a + lo) * 1000) + 3;
    while (to >= kLo && !(a >= startAt(to) - tol)) to--;
    let from = Math.ceil((b - lo) * 1000) - 3;
    while (from <= kHi && !(b <= startAt(from) + extent + tol)) from++;
    return [Math.max(kLo, from), Math.min(kHi, to)];
  };
  const otherStart = 0.5 - otherExtent / 2;
  const spans: FaceRuns[] = faces.map((f) => {
    const x = axis === 'x';
    const a = x ? f.x : f.y;
    const len = x ? f.w : f.h;
    const o = x ? f.y : f.x;
    const lead = (x ? EXPAND.left : EXPAND.up) * len;
    const grownA = clamp01(a - lead);
    const grownB = clamp01(a - lead + (x ? EXPAND.width : EXPAND.height) * len);
    const fits = within(o, o + (x ? f.h : f.w), otherStart, otherExtent);
    return {
      raw: fits ? runOf(a, a + len, TOL) : [kHi + 1, kLo - 1],
      grown: runOf(grownA, grownB, GROWN_TOL),
      weight: f.w * f.h,
      grownMid: (grownA + grownB) / 2,
      mid: a + len / 2,
      grownA,
      grownB,
    };
  });
  const score = (k: number): Keep => {
    const out: Keep = [0, 0, 0];
    for (const s of spans) {
      if (inRun(k, s.raw)) {
        out[0]++;
        out[1] += s.weight;
      }
      if (inRun(k, s.grown)) out[2] += s.weight;
    }
    return out;
  };

  // What a window keeps changes only at the ends of the runs.
  const candidates = new Set<number>([kLo, kHi]);
  for (const s of spans) {
    for (const k of [...s.raw, ...s.grown]) if (k >= kLo && k <= kHi) candidates.add(k);
  }
  let best: Keep = [-1, -1, -1];
  let bestAt: number[] = [];
  for (const k of candidates) {
    const cmp = better(score(k), best);
    if (cmp > 0) {
      best = score(k);
      bestAt = [k];
    } else if (cmp === 0) {
      bestAt.push(k);
    }
  }

  const target = weightedMean(spans, (s) => s.grownMid);
  let chosen = kLo;
  let distance = Infinity;
  const seen = new Set<number>();
  for (const at of bestAt) {
    // The run of this kept set: where every one of its faces stays kept.
    const kept = spans.filter((s) => inRun(at, s.raw));
    let from = kLo;
    let to = kHi;
    for (const s of spans) {
      if (inRun(at, s.raw)) [from, to] = [Math.max(from, s.raw[0]), Math.min(to, s.raw[1])];
      if (inRun(at, s.grown)) [from, to] = [Math.max(from, s.grown[0]), Math.min(to, s.grown[1])];
    }
    if (seen.has(from)) continue;
    seen.add(from);
    let want: number;
    if (kept.length > 0) {
      const top = Math.min(...kept.map((s) => s.grownA));
      const bottom = Math.max(...kept.map((s) => s.grownB));
      const slack = extent - (bottom - top);
      want = axis === 'y' && slack > 0 ? top - HEADROOM * slack + extent / 2 : weightedMean(kept, (s) => s.grownMid);
    } else {
      // No face fits whole anywhere: show the middle of the faces themselves.
      want = weightedMean(spans, (s) => s.mid);
    }
    const k = Math.max(from, Math.min(to, Math.round(want * 1000)));
    const d = Math.abs(k / 1000 - target);
    if (d < distance - 1e-12 || (Math.abs(d - distance) <= 1e-12 && k < chosen)) {
      distance = d;
      chosen = k;
    }
  }
  return chosen / 1000;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
